/**
 * Documents WebSocket wire-shape constants for the dispatcher-UI badge
 * surface. Backend gateway
 * (`backend-tms/src/documents/gateways/documents.gateway.ts`) emits
 * these events to the per-tenant `tenant:{id}:documents` room and the
 * frontend hook (`apps/web-tms/src/hooks/use-documents-badge.ts`)
 * subscribes to them — both sides import from this file so a rename
 * touches one place.
 *
 * Status string convention on the wire is lowercase to match the
 * frontend hook's hardcoded checks (`'pending'`, `'approved'`,
 * `'rejected'`). Backend `DocumentApprovalStatus` enum values are
 * uppercase (`'PENDING'`, `'APPROVED'`, etc.); the gateway lowercases
 * on emit. Mirrors the `FinanceGateway` precedent.
 *
 * Issue #420.
 */

/**
 * Room-scope tag passed to `TenantRoomManager` when stamping the
 * tenant room name (`tenant:{id}:documents`). Mirrors
 * `FINANCE_WS_ROOM_SCOPE` precedent.
 */
export const DOCUMENTS_WS_ROOM_SCOPE = 'documents' as const;

/**
 * Wire event names emitted on the `documents` WebSocket namespace.
 *
 * - `PENDING_COUNT` is sent once per connection (initial badge
 *   snapshot) and re-sent when a status change invalidates the count.
 * - `DOCUMENT_SUBMITTED` fires when a document transitions
 *   `NOT_REQUIRED|REJECTED → PENDING` (a fresh submission). The
 *   frontend increments the badge by 1.
 * - `DOCUMENT_STATUS_CHANGED` fires on every approval-state transition;
 *   the frontend hook decides increment/decrement based on the wire
 *   `status` value.
 * - `ERROR` is gateway-initiated only (handshake JWT verification fail).
 */
export const DOCUMENTS_WS_EVENTS = {
  PENDING_COUNT: 'documents:pending-count',
  DOCUMENT_SUBMITTED: 'document:submitted',
  DOCUMENT_STATUS_CHANGED: 'document:status-changed',
  ERROR: 'error',
} as const;

export type DocumentsWsEventName = (typeof DOCUMENTS_WS_EVENTS)[keyof typeof DOCUMENTS_WS_EVENTS];

/**
 * Lowercase wire-status values the gateway relays. Frontend hook
 * checks against these literals; defining them here pins the contract.
 */
export const DOCUMENTS_WIRE_STATUSES = {
  NOT_REQUIRED: 'not_required',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

export type DocumentsWireStatus =
  (typeof DOCUMENTS_WIRE_STATUSES)[keyof typeof DOCUMENTS_WIRE_STATUSES];

/**
 * Payload for the `document:submitted` event. Carries enough context
 * for the frontend to surface a toast / queue entry in addition to the
 * count increment.
 */
export interface DocumentSubmittedPayload {
  readonly documentId: string;
  readonly submittedBy?: string;
}

/**
 * Payload for the `document:status-changed` event. `previousStatus` is
 * optional because some emit paths (e.g. retro-active backfill) don't
 * carry a "from" value.
 */
export interface DocumentStatusChangedPayload {
  readonly documentId: string;
  readonly previousStatus?: DocumentsWireStatus;
  readonly status: DocumentsWireStatus;
}
